import { API } from 'lib/api';
import { BUDGETING_ENDPOINTS } from 'constant/endpoints/budgeting';
import { PROJECT_ENDPOINTS } from 'constant/endpoints/project';
import type {
  EditableBudget,
  IBudgetCapabilityResponse,
  IBudgetPreviewResponse,
  IBudgetUploadResponse,
  IBudgetZoneListResponse,
  IProjectCostCentresResponse,
} from 'pages/BudgetWizard/types/response';

// The server-authoritative budgeting capability. The client uses this purely to
// decide what to display; the backend enforces the flag on every write.
export const fetchBudgetingCapabilities =
  async (): Promise<IBudgetCapabilityResponse> => {
    const { data } = await API.get<IBudgetCapabilityResponse>(
      BUDGETING_ENDPOINTS.GET_CAPABILITIES,
    );
    return data;
  };

// A project's zones. Absent zones (empty list) means the project has no zone
// dimension, so the wizard hides its zone step (Alec's rule, kata 951v).
export const fetchProjectZones = async (
  projectId: string,
): Promise<IBudgetZoneListResponse> => {
  const { data } = await API.get<IBudgetZoneListResponse>(
    BUDGETING_ENDPOINTS.LIST_ZONES(projectId),
  );
  return data;
};

// Upload a budget workbook for a project. The project id is a URL path segment
// (VDP-1056 project-scoped standard) and the workbook is sent as the multipart
// `file` part the backend requires. Dimension mappings (cost centres / resource
// categories) belong to later wizard steps and are not sent here; the server
// defaults them to empty. The server is authoritative on file validity — it
// answers 201 with the persisted budget reference, 422 with `errors[]`, or 409
// on a version conflict.
export const uploadBudgetWorkbook = async (
  projectId: string,
  file: File,
): Promise<IBudgetUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await API.post<IBudgetUploadResponse>(
    BUDGETING_ENDPOINTS.UPLOAD_BUDGET(projectId),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
};

// Preview a budget workbook for a project WITHOUT persisting anything. Same
// endpoint as the save path, but with the `commit=false` form field, which
// selects the backend's preview mode: it parses, validates and resolves the
// file and returns the resolved budget in the stable read shape (`BudgetView`),
// saving nothing (VDP-1049). The wizard uses this on the Upload step so nothing
// is committed until the final Confirm step (which POSTs `commit=true`). Errors
// surface identically to the save path (422 `errors[]`, 400 bad file); a 409
// version conflict cannot occur because nothing is written.
export const previewBudgetWorkbook = async (
  projectId: string,
  file: File,
): Promise<IBudgetPreviewResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  // Absent `commit` would default to a save; send it explicitly so a preview is
  // never a persist by omission.
  formData.append('commit', 'false');
  const { data } = await API.post<IBudgetPreviewResponse>(
    BUDGETING_ENDPOINTS.UPLOAD_BUDGET(projectId),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
};

// Save a manually-built budget for a project. The whole flat `EditableBudget`
// is the JSON body; the project id is a URL path segment (VDP-1056 project-
// scoped standard). The server is authoritative and answers 201 with the
// persisted budget's `reference`.
export const saveBudget = async (
  projectId: string,
  body: EditableBudget,
): Promise<IBudgetUploadResponse> => {
  const { data } = await API.post<IBudgetUploadResponse>(
    BUDGETING_ENDPOINTS.SAVE_BUDGET(projectId),
    body,
  );
  return data;
};

// Read a persisted budget in the flat, grid-editable shape (`EditableBudget`),
// as opposed to the grouped read view. Both project id and reference are path
// segments. The grid loads this to populate its rows for editing.
export const fetchBudgetForEdit = async (
  projectId: string,
  reference: string,
): Promise<EditableBudget> => {
  const { data } = await API.get<EditableBudget>(
    BUDGETING_ENDPOINTS.GET_BUDGET_FOR_EDIT(projectId, reference),
  );
  return data;
};

// A project's cost centres, grouped into `assigned` (already on the project)
// and `unassigned`. Reuses the existing project-scoped dropdown endpoint
// (`v1.1/project_drop_down_cost_centres/`) with `group_by=true` so the response
// is the `{ assigned, unassigned }` shape the shared `useCostCentreOptions`
// transformer consumes. Scoping is by the `project_id` query param (the
// endpoint predates the path-segment standard).
export const fetchProjectCostCentres = async (
  projectId: string,
): Promise<IProjectCostCentresResponse> => {
  const { data } = await API.get<IProjectCostCentresResponse>(
    PROJECT_ENDPOINTS.GET_PROJECT_COST_CENTRES,
    { params: { project_id: projectId, group_by: true } },
  );
  return data;
};
